'use client';

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import NextLink from 'next/link';
import { varAlpha } from '../../theme/styles';
import { RADIUS } from '@/theme/brand-tokens';
import type { VoiceTrialSeverity } from '@/types/voice-subscription';

export type VoiceTrialPanelProps = {
  used: number;
  remaining: number;
  limit: number;
  severity: VoiceTrialSeverity;
  href: string;
  sx?: SxProps<Theme>;
  /** Center the text and use a single text color throughout — for the mini-nav flyout footer */
  centered?: boolean;
};

// Persistent "free voice sessions remaining" indicator for the voice module
// sidebar — mirrors the same trial ledger surfaced on the campaign overview
// tab so it's visible no matter which voice page the user is on.
export function VoiceTrialPanel({ used, remaining, limit, severity, href, sx, centered }: VoiceTrialPanelProps) {
  const theme = useTheme();
  const progress = limit > 0 ? Math.min(100, Math.round((remaining / limit) * 100)) : 0;
  const textColor = centered ? 'text.primary' : undefined;

  return (
    <NextLink href={href} style={{ textDecoration: 'none' }}>
      <Box
        sx={{
          mx: 1,
          my: 1,
          p: 2,
          borderRadius: `${RADIUS.md}px`,
          bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.12),
          border: `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
          cursor: 'pointer',
          transition: theme.transitions.create(['background-color', 'border-color']),
          '&:hover': {
            bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.18),
            borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.24),
          },
          ...(centered && { textAlign: 'center' }),
          ...sx,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: textColor ?? 'text.primary',
            letterSpacing: 0.8,
            fontWeight: 600,
            fontSize: '0.75rem',
            display: 'block',
            textTransform: 'uppercase',
          }}
        >
          FREE VOICE SESSIONS
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            my: 0.5,
            color: 'text.primary',
          }}
        >
          {remaining}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: textColor ?? 'text.primary',
            display: 'block',
            mb: 1.5,
            fontSize: '0.8125rem',
          }}
        >
          of {limit} remaining
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          color={severity}
          sx={{
            height: 5,
            borderRadius: `${RADIUS.xs}px`,
            bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.2),
            '& .MuiLinearProgress-bar': {
              borderRadius: `${RADIUS.xs}px`,
            },
          }}
        />
      </Box>
    </NextLink>
  );
}
